/* eslint-disable max-lines */
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import ErrorBoundary from 'components/ErrorBoundary';
import { Container, InputWrapper, ClearButton } from './styled';
import { AutocompleteProps } from './types';
import { useTextMeasurement, useResizeObserver, useClickOutside, useTagCalculation } from './hooks';
import { Tags, portalId } from './components';
import VirtualDropdown from './components/VirtualDropdown';

const AutocompleteMain: React.FC<AutocompleteProps> = React.memo(
  ({
    placeholder,
    data = [],
    value = [],
    onChange,
    disabled = false,
    className,
    allowCustomOptions = true,
    styles,
    small,
    onClose,
    usePortal = false,
  }) => {
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [containerWidth, setContainerWidth] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const previousDataRef = useRef(data);
    const previousValueRef = useRef(value);
    const filteredDataCache = useRef<{ input: string; data: string[]; result: string[] }>({
      input: '',
      data: [],
      result: [],
    });

    const { measureTextWidth } = useTextMeasurement();

    const containerRef = useResizeObserver(() => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    });

    // Close dropdown and call onClose
    const closeDropdown = useCallback(() => {
      setIsOpen(false);
      setFocusedIndex(-1);
      onClose?.();
    }, [onClose]);

    useClickOutside(
      containerRef,
      () => {
        if (isOpen) {
          closeDropdown();
        }
      },
      usePortal ? portalId : undefined,
    );

    // Check if data actually changed
    const hasDataChanged = useMemo(() => {
      if (previousDataRef.current.length !== data.length) {
        previousDataRef.current = [...data];
        return true;
      }

      const changed = previousDataRef.current.some((item, index) => item !== data[index]);
      if (changed) {
        previousDataRef.current = [...data];
      }
      return changed;
    }, [data]);

    // Check if value actually changed
    const hasValueChanged = useMemo(() => {
      if (previousValueRef.current.length !== value.length) {
        previousValueRef.current = [...value];
        return true;
      }

      const changed = previousValueRef.current.some((item, index) => item !== value[index]);
      if (changed) {
        previousValueRef.current = [...value];
      }
      return changed;
    }, [value]);

    // Clear cache when data changes
    useEffect(() => {
      if (hasDataChanged) {
        filteredDataCache.current = { input: '', data: [], result: [] };
      }
    }, [hasDataChanged]);

    // Optimize filtered data with caching
    const filteredData = useMemo(() => {
      const currentInput = inputValue.trim();

      // Check cache first
      if (
        !hasDataChanged &&
        filteredDataCache.current.input === currentInput &&
        filteredDataCache.current.data === data
      ) {
        return filteredDataCache.current.result;
      }
      let result: string[];
      if (!currentInput) {
        // Return original data if no input, but limit for performance
        result = data.slice(0, 100);
      } else {
        const searchTerm = currentInput.toLowerCase();
        const filtered = data.filter((item: string) => item.toLowerCase().includes(searchTerm));
        // Limit results for better performance
        result = filtered.slice(0, 50);
      }
      // Update cache
      filteredDataCache.current = {
        input: currentInput,
        data: data,
        result: result,
      };
      return result;
    }, [data, inputValue, hasDataChanged]);

    const tagCalculation = useTagCalculation({
      value,
      measureTextWidth,
      containerWidth,
    });
    // Reset focused index when value changes externally
    useEffect(() => {
      if (hasValueChanged) {
        setFocusedIndex(-1);
      }
    }, [hasValueChanged]);

    // Reset input and focused index when data changes significantly
    useEffect(() => {
      if (hasDataChanged) {
        setInputValue('');
        setFocusedIndex(-1);
        // Optionally close dropdown if data changed externally
        if (isOpen) {
          setIsOpen(false);
        }
      }
    }, [hasDataChanged, isOpen]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setIsOpen(true);
      setFocusedIndex(1); // Start from first option (after input)
    }, []);

    const handleSelectOption = useCallback(
      (option: string) => {
        const newValue = value.includes(option) ? value.filter((item) => item !== option) : [...value, option];

        onChange?.(newValue);
        setInputValue('');
      },
      [value, onChange],
    );

    const handleRemoveTag = useCallback(
      (index: number) => {
        const newValue = value.filter((_, i) => i !== index);
        onChange?.(newValue);
      },
      [value, onChange],
    );

    const handleClearAll = useCallback(
      (e: React.MouseEvent) => {
        if (disabled) return; // Add this check
        e.stopPropagation();
        onChange?.([]);
        setInputValue('');
        setFocusedIndex(-1);
        inputRef.current?.focus();
      },
      [onChange, disabled], // Add disabled to dependencies
    );


    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
          closeDropdown();
          inputRef.current?.blur();
        } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
          handleRemoveTag(value.length - 1);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
            setFocusedIndex(1); // Start from first option
          } else {
            // focusedIndex 0 is input, 1+ are options
            const maxIndex = filteredData.length; // Since input is at index 0
            setFocusedIndex((prev) => (prev < maxIndex ? prev + 1 : 1));
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (isOpen) {
            const maxIndex = filteredData.length;
            setFocusedIndex((prev) => (prev > 1 ? prev - 1 : maxIndex));
          }
        } else if (e.key === 'Enter') {
          e.preventDefault();

          if (isOpen && focusedIndex > 0 && focusedIndex <= filteredData.length) {
            const selectedOption = filteredData[focusedIndex - 1]; // Adjust for input at index 0
            handleSelectOption(selectedOption);
            setFocusedIndex(1);
          } else if (inputValue.trim() && allowCustomOptions) {
            const trimmedValue = inputValue.trim();
            if (trimmedValue && !value.includes(trimmedValue)) {
              const newValue = [...value, trimmedValue];
              onChange?.(newValue);
              setInputValue('');
              setFocusedIndex(1);
            }
          }
        } else if (e.key === 'Tab') {
          closeDropdown();
        }
      },
      [
        inputValue,
        value,
        handleRemoveTag,
        onChange,
        allowCustomOptions,
        isOpen,
        focusedIndex,
        filteredData,
        handleSelectOption,
        closeDropdown,
      ],
    );

    const handleContainerClick = useCallback(() => {
      if (disabled) return; // Add this check
      setIsOpen(true);
      // Focus the input in dropdown after a brief delay
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }, [disabled]); // Add disabled to dependencies

    const hasValue = value.length > 0;
    const dropdownId = useMemo(() => `autocomplete-dropdown-${Math.random().toString(36).substr(2, 9)}`, []);

    return (
      <ErrorBoundary>
        <Container ref={containerRef} className={className} style={styles?.container}>
          <InputWrapper
            style={styles?.inputWrapper}
            isOpen={isOpen && !disabled} // Prevent open state when disabled
            small={small}
            onClick={handleContainerClick}
            disabled={disabled}
          >
            <Tags
              tagCalculation={tagCalculation}
              value={value}
              onRemoveTag={handleRemoveTag}
              styles={styles}
              small={small}
              disabled={disabled} // Pass disabled to Tags
            />
            {/* Only show clear button when not disabled and has value */}
            {hasValue && !disabled && (
              <ClearButton
                type="button"
                onClick={handleClearAll}
                small={small}
                aria-label="Clear all selected options"
                title="Clear all"
              >
                ✕
              </ClearButton>
            )}
          </InputWrapper>
          <VirtualDropdown
            isOpen={isOpen && !disabled} // Prevent dropdown when disabled
            filteredData={filteredData}
            value={value}
            onSelectOption={handleSelectOption}
            focusedIndex={focusedIndex}
            dropdownId={dropdownId}
            styles={styles}
            small={small}
            usePortal={usePortal}
            anchorElement={containerRef}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onInputKeyDown={handleKeyDown}
            inputRef={inputRef}
            placeholder={placeholder}
            disabled={disabled} // Pass disabled to VirtualDropdown
          />
        </Container>
      </ErrorBoundary>
    );
  },
  // Improved comparison function for React.memo
  (prevProps, nextProps) => {
    // Handle undefined/null data arrays
    const prevData = prevProps.data || [];
    const nextData = nextProps.data || [];
    const dataChanged = prevData.length !== nextData.length || prevData.some((item, index) => item !== nextData[index]);
    // Handle undefined/null value arrays
    const prevValue = prevProps.value || [];
    const nextValue = nextProps.value || [];
    const valueChanged =
      prevValue.length !== nextValue.length || prevValue.some((item, index) => item !== nextValue[index]);
    return (
      !dataChanged &&
      !valueChanged &&
      prevProps.disabled === nextProps.disabled
    );
  },
);

AutocompleteMain.displayName = 'AutocompleteMain';

export default AutocompleteMain;
